import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEventsByIds } from '../services/eventService';
import { useLocale } from '../i18n/LocaleContext';
import { useFavourites } from '../favourites/FavouritesContext';
import AppHeader from '../components/AppHeader';
import EventCard from '../components/EventCard';
import ScrollToTop from '../components/ScrollToTop';
import { SkeletonGrid } from '../components/States';
import { HeartIcon } from '../components/Icons';
import { isPastEvent, mergeFavourites } from '../utils/favouriteSnapshot';
import { parseEventDate } from '../utils/format';
import { useDocumentTitle } from '../utils/useDocumentTitle';

export default function FavouritesPage() {
  const navigate = useNavigate();
  const { t } = useLocale();
  const { favourites, clearPast } = useFavourites();

  useDocumentTitle(`${t('favourites.title')} · ${t('app.name')}`);

  const [fresh, setFresh] = useState(null);
  const [loading, setLoading] = useState(false);

  const ids = useMemo(() => favourites.map((entry) => entry.id), [favourites]);
  const idsKey = ids.join(',');

  // Snapshots render immediately; this replaces them with current prices and
  // ticket status, and reveals which saved events have been delisted.
  const refresh = useCallback(async () => {
    if (ids.length === 0) {
      setFresh([]);
      return;
    }
    setLoading(true);
    try {
      setFresh(await getEventsByIds(ids));
    } catch (error) {
      console.warn('Could not refresh favourites:', error.message);
      setFresh(null);
    } finally {
      setLoading(false);
    }
  }, [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refresh();
    window.scrollTo(0, 0);
  }, [refresh]);

  const rows = useMemo(
    () => (fresh === null ? favourites.map((entry) => ({ ...entry, unavailable: false }))
      : mergeFavourites(favourites, fresh)),
    [favourites, fresh]
  );

  // Upcoming first, soonest at the top; anything already over drops to the end.
  const sorted = useMemo(() => {
    const withMeta = rows.map((row) => ({ ...row, past: isPastEvent(row.event) }));
    return withMeta.sort((a, b) => {
      if (a.past !== b.past) {
        return a.past ? 1 : -1;
      }
      const timeA = parseEventDate(a.event.date)?.getTime() ?? 0;
      const timeB = parseEventDate(b.event.date)?.getTime() ?? 0;
      return timeA - timeB;
    });
  }, [rows]);

  const pastCount = sorted.filter((row) => row.past).length;

  return (
    <div className="app">
      <AppHeader />

      <main className="page favourites">
        <div className="results-bar">
          <h1 className="favourites__title">{t('favourites.title')}</h1>
          {favourites.length > 0 && (
            <span className="results-bar__count">
              {t('favourites.count', { count: favourites.length })}
            </span>
          )}
        </div>

        {favourites.length === 0 ? (
          <div className="state">
            <span className="state__icon">
              <HeartIcon size={24} />
            </span>
            <h2 className="state__title">{t('favourites.empty.title')}</h2>
            <p className="state__body">{t('favourites.empty.body')}</p>
            <button type="button" className="btn btn--accent" onClick={() => navigate('/')}>
              {t('favourites.empty.browse')}
            </button>
          </div>
        ) : loading && fresh === null ? (
          <SkeletonGrid count={3} />
        ) : (
          <>
            {pastCount > 0 && (
              <div className="favourites__tools">
                <button type="button" className="btn btn--ghost" onClick={clearPast}>
                  {t('favourites.clearPast')} ({pastCount})
                </button>
              </div>
            )}

            <div className="events-grid">
              {sorted.map((row) => (
                <EventCard
                  key={row.id}
                  event={row.event}
                  muted={row.past || row.unavailable}
                  note={
                    row.unavailable
                      ? t('favourites.unavailable')
                      : row.past
                        ? t('favourites.past')
                        : null
                  }
                  onClick={() =>
                    row.unavailable ? undefined : navigate(`/event/${row.id}`)
                  }
                />
              ))}
            </div>
          </>
        )}
      </main>

      <ScrollToTop />
    </div>
  );
}
